import {
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Select,
  Space,
} from 'antd'
import { useState } from 'react'
import { useCasesListQuery } from '../Cases/casesAdminQueries'
import { useTagsListQuery } from '../Tags/tagsAdminQueries'
import {
  buildPresetCreateInput,
  buildPresetUpdateInput,
  emptyPresetForm,
  EvalRunPreset,
  isPresetSelectionEmpty,
  PresetForm,
  presetFormFromPreset,
  validatePresetName,
} from './presetsAdminModel'
import {
  useCreatePresetMutation,
  useUpdatePresetMutation,
} from './presetsAdminQueries'

type Props = {
  open: boolean
  onClose: () => void
  /** When provided the modal edits this preset; otherwise it creates one. */
  preset?: EvalRunPreset
  /** Seed for a new preset (e.g. "Save as preset" from the run-create modal). */
  initialForm?: Partial<PresetForm>
}

// The pickers are admin-scale; load the first page at the backend's cap.
const TAG_FILTERS = { page: 1, pageSize: 50 }

const PresetEditorModal = ({ open, onClose, preset, initialForm }: Props) => {
  const isEdit = preset != null
  const [form, setForm] = useState<PresetForm>(() =>
    preset ? presetFormFromPreset(preset) : { ...emptyPresetForm(), ...initialForm },
  )
  const set = <K extends keyof PresetForm>(key: K, value: PresetForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const createMutation = useCreatePresetMutation()
  const updateMutation = useUpdatePresetMutation()
  const mutation = isEdit ? updateMutation : createMutation

  const { data: casesData } = useCasesListQuery()
  const { data: tagData } = useTagsListQuery(TAG_FILTERS)
  const caseOptions =
    casesData?.cases.map((c) => ({ label: c.name, value: c.case_id })) ?? []
  const tagOptions =
    tagData?.tags.map((t) => ({ label: t.name, value: t.tag_id })) ?? []

  const selectionEmpty = isPresetSelectionEmpty(form)

  const handleClose = () => {
    setForm(preset ? presetFormFromPreset(preset) : { ...emptyPresetForm(), ...initialForm })
    onClose()
  }

  const handleOk = () => {
    const nameError = validatePresetName(form.name)
    if (nameError) {
      notification.error({ message: 'Invalid preset', description: nameError })
      return
    }
    if (isEdit && preset) {
      updateMutation.mutate(buildPresetUpdateInput(preset, form), {
        onSuccess: handleClose,
      })
      return
    }
    createMutation.mutate(buildPresetCreateInput(form), {
      onSuccess: handleClose,
    })
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      onOk={handleOk}
      okText="Save preset"
      okButtonProps={{ disabled: selectionEmpty }}
      title={isEdit ? 'Edit Preset' : 'New Preset'}
      confirmLoading={mutation.isLoading}
      destroyOnClose
    >
      <Form layout="vertical">
        <Form.Item label="Name" required style={{ marginBottom: 12 }}>
          <Input
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. nightly-security"
            autoFocus
          />
        </Form.Item>
        <Form.Item
          label="Cases"
          // Cases and tags combine as a union — re-resolved fresh at each launch.
          extra="Pick cases to run directly."
          style={{ marginBottom: 12 }}
        >
          <Select
            mode="multiple"
            allowClear
            value={form.caseIds}
            onChange={(value) => set('caseIds', value)}
            options={caseOptions}
            placeholder="Select cases"
            optionFilterProp="label"
            notFoundContent="No cases — create some under Cases first."
          />
        </Form.Item>
        <Form.Item
          label="Tags"
          extra="Every case carrying a selected tag is included at launch — including newly-tagged cases."
          style={{ marginBottom: 12 }}
        >
          <Select
            mode="multiple"
            allowClear
            value={form.tagIds}
            onChange={(value) => set('tagIds', value)}
            options={tagOptions}
            placeholder="Select tags"
            optionFilterProp="label"
            notFoundContent="No tags — create some under Tags first."
          />
        </Form.Item>
        <Space size="large" align="center">
          <Checkbox
            checked={form.includeMutants}
            onChange={(e) => set('includeMutants', e.target.checked)}
          >
            Include mutants
          </Checkbox>
          <span>
            Trials per case{' '}
            <InputNumber
              min={1}
              value={form.trials}
              onChange={(value) => set('trials', value ?? 1)}
            />
          </span>
        </Space>
      </Form>
    </Modal>
  )
}

export default PresetEditorModal
